export const TUTTI_COMPUTER_API_BASE_PATH =
  '/api/desktop/v2/admin/tutti-v2/computers';

export const TUTTI_COMPUTER_CONNECTION_WIRE_VALUE = {
  online: 1,
  offline: 2,
} as const;

export const RUNTIME_LOG_RANGE_WIRE_VALUE = {
  '1_hour': 1,
  '3_hours': 2,
  '1_day': 3,
  '3_days': 4,
  '14_days': 5,
} as const;

function normalizedEnum(value: unknown): string {
  return typeof value === 'string' ? value.trim().toUpperCase() : '';
}

export function normalizeComputerConnectionStatus(value: unknown) {
  const name = normalizedEnum(value);
  if (
    value === 1 ||
    value === '1' ||
    name === 'ONLINE' ||
    name.endsWith('_ONLINE')
  ) {
    return 'online' as const;
  }
  if (
    value === 2 ||
    value === '2' ||
    name === 'OFFLINE' ||
    name.endsWith('_OFFLINE')
  ) {
    return 'offline' as const;
  }
  return 'unknown' as const;
}

export function normalizeRuntimeLogRangePreset(value: unknown) {
  const name = normalizedEnum(value);
  if (value === 1 || value === '1' || name.endsWith('_1_HOUR')) {
    return '1_hour' as const;
  }
  if (value === 2 || value === '2' || name.endsWith('_3_HOURS')) {
    return '3_hours' as const;
  }
  if (value === 3 || value === '3' || name.endsWith('_1_DAY')) {
    return '1_day' as const;
  }
  if (value === 4 || value === '4' || name.endsWith('_3_DAYS')) {
    return '3_days' as const;
  }
  if (value === 5 || value === '5' || name.endsWith('_14_DAYS')) {
    return '14_days' as const;
  }
  return 'unknown' as const;
}

export function normalizeRuntimeLogRecallState(value: unknown) {
  const name = normalizedEnum(value);
  if (value === 1 || value === '1' || name.endsWith('_WAITING_FOR_DEVICE')) {
    return 'waiting_for_device' as const;
  }
  if (value === 2 || value === '2' || name.endsWith('_DEVICE_PROCESSING')) {
    return 'device_processing' as const;
  }
  if (value === 3 || value === '3' || name.endsWith('_DOWNLOADABLE')) {
    return 'downloadable' as const;
  }
  if (value === 4 || value === '4' || name.endsWith('_FAILED')) {
    return 'failed' as const;
  }
  return 'unknown' as const;
}

export function normalizeRuntimeLogUploadMode(value: unknown) {
  const name = normalizedEnum(value);
  if (value === 1 || value === '1' || name.endsWith('_SINGLE_PUT')) {
    return 'single_put' as const;
  }
  if (value === 2 || value === '2' || name.endsWith('_MULTIPART')) {
    return 'multipart' as const;
  }
  return 'unknown' as const;
}

export function isActiveRuntimeLogRecallState(value: unknown) {
  const state = normalizeRuntimeLogRecallState(value);
  return state === 'waiting_for_device' || state === 'device_processing';
}

export function tuttiComputerPath(computerId?: string) {
  if (!computerId) {
    return TUTTI_COMPUTER_API_BASE_PATH;
  }
  return `${TUTTI_COMPUTER_API_BASE_PATH}/${encodeURIComponent(computerId)}`;
}

export function runtimeLogRecallCollectionPath(computerId: string) {
  return `${tuttiComputerPath(computerId)}/runtime-log-recalls`;
}

export function runtimeLogRecallPath(computerId: string, taskId: string) {
  return `${runtimeLogRecallCollectionPath(computerId)}/${encodeURIComponent(taskId)}`;
}

export function runtimeLogRecallDownloadPath(
  computerId: string,
  taskId: string,
) {
  return `${runtimeLogRecallPath(computerId, taskId)}:create-download-url`;
}

export function diagnosticTargetPath() {
  return `${TUTTI_COMPUTER_API_BASE_PATH}:resolve-diagnostic-target`;
}

export function executionDiagnosticsPath(computerId: string) {
  return `${tuttiComputerPath(computerId)}/execution-diagnostics`;
}

export function computerExecutionDiagnosticFailure(
  error: unknown,
): 'ambiguous_turn' | 'not_found' | null {
  if (typeof error !== 'object' || error === null || !('response' in error))
    return null;
  const response = error.response;
  if (typeof response !== 'object' || response === null) return null;
  if ('status' in response && response.status === 404) return 'not_found';
  if (
    !('status' in response) ||
    response.status !== 409 ||
    !('data' in response)
  )
    return null;
  const data = response.data;
  if (
    typeof data === 'object' &&
    data !== null &&
    'reason' in data &&
    data.reason === 'COMPUTER_DIAGNOSTIC_TURN_AMBIGUOUS'
  )
    return 'ambiguous_turn';
  return null;
}
